import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { useAuth } from '../../contexts/authContext';
import { useAlert } from '../../contexts/alertContext';
import { useTitle } from '../../hooks/titleProvider';
import { hasWorkspaceAccess, Roles } from '../../helpers/access';
import PrimaryButton from '../ui/buttons/primaryButton';
import ticketService from '../../features/support/ticketService';

const TICKET_TYPE_OPTIONS = [
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature Request' },
  { value: 'general', label: 'General Enquiry' }
];

const TICKET_TYPE_LABELS = {
  bug: 'Bug',
  feature: 'Feature Request',
  general: 'General Enquiry'
};

const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved'
};

const STATUS_COLORS = {
  open: 'warning',
  in_progress: 'info',
  resolved: 'success'
};

const toDateLabel = (value) => {
  if (!value) return '';

  let date;
  if (typeof value?.toDate === 'function') date = value.toDate();
  else if (typeof value?.seconds === 'number') date = new Date(value.seconds * 1000);
  else if (typeof value?._seconds === 'number') date = new Date(value._seconds * 1000);
  else date = new Date(value);

  return date instanceof Date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : '';
};

const truncate = (value, max = 80) => {
  const text = String(value || '');
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const statusChip = (status) => (
  <Chip
    size="small"
    variant="outlined"
    color={STATUS_COLORS[status] || 'warning'}
    label={STATUS_LABELS[status] || status}
  />
);

const Support = () => {
  const { setTitle } = useTitle();
  const { currentUserProfile, currentWorkspace } = useAuth();
  const { showAlertSuccess, showAlertError } = useAlert();
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolvingTicket, setResolvingTicket] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [resolving, setResolving] = useState(false);

  const isSuperuser = hasWorkspaceAccess(currentUserProfile, currentWorkspace, Roles.SUPERUSER);

  useEffect(() => {
    setTitle('Support');
  }, [setTitle]);

  const fetchTickets = () => {
    setLoading(true);

    return (isSuperuser ? ticketService.getAllTickets() : ticketService.getMyTickets())
      .then((list) => {
        setTickets(Array.isArray(list) ? list : []);
      })
      .catch((error) => {
        showAlertError(error?.response?.data?.message || 'Unable to load tickets.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperuser]);

  const handleSubmit = async () => {
    if (submitting) {
      return;
    }

    try {
      setSubmitting(true);
      await ticketService.createTicket({ type, message });
      setMessage('');
      showAlertSuccess(
        'Your support ticket has been created. We will email you when it is resolved.'
      );
      await fetchTickets();
    } catch (error) {
      showAlertError(error?.response?.data?.message || 'Unable to create your support ticket.');
    } finally {
      setSubmitting(false);
    }
  };

  const openResolveDialog = (ticket) => {
    setResolvingTicket(ticket);
    setResolutionNote('');
  };

  const closeResolveDialog = () => {
    setResolvingTicket(null);
    setResolutionNote('');
  };

  const handleResolve = async () => {
    const note = resolutionNote.trim();

    if (!resolvingTicket || !note || resolving) {
      return;
    }

    try {
      setResolving(true);
      await ticketService.resolveTicket(resolvingTicket.id, note);
      closeResolveDialog();
      showAlertSuccess('Ticket resolved and the user has been notified.');
      await fetchTickets();
    } catch (error) {
      showAlertError(error?.response?.data?.message || 'Unable to resolve this ticket.');
    } finally {
      setResolving(false);
    }
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">Contact Support</Typography>
              <Typography variant="body2" color="text.secondary">
                Describe the issue, feature request or enquiry and we will get back to you by
                email.
              </Typography>

              <TextField
                label="First name"
                value={currentUserProfile?.firstName || ''}
                size="small"
                disabled
                fullWidth
              />
              <TextField
                label="Last name"
                value={currentUserProfile?.lastName || ''}
                size="small"
                disabled
                fullWidth
              />
              <TextField
                label="Email"
                value={currentUserProfile?.email || ''}
                size="small"
                disabled
                fullWidth
              />

              <TextField
                select
                label="Type"
                value={type}
                size="small"
                onChange={(event) => setType(event.target.value)}
                fullWidth>
                {TICKET_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Message"
                placeholder="Describe the issue, feature request or enquiry."
                value={message}
                size="small"
                onChange={(event) => setMessage(event.target.value)}
                multiline
                minRows={5}
                fullWidth
              />

              <Box display="flex" alignItems="center" gap={1.5}>
                <PrimaryButton variant="contained" onClick={handleSubmit} disabled={submitting}>
                  Submit Ticket
                </PrimaryButton>
                {submitting ? <CircularProgress size={18} /> : null}
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                <Typography variant="h6">{isSuperuser ? 'All Tickets' : 'My Tickets'}</Typography>
                <Button size="small" variant="outlined" onClick={fetchTickets} disabled={loading}>
                  Refresh
                </Button>
              </Box>

              {loading ? (
                <Box display={'flex'} justifyContent={'center'} p={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : tickets.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No tickets yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Message</TableCell>
                        {isSuperuser ? <TableCell>Actions</TableCell> : null}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tickets.map((ticket) => {
                        // The monitor fills englishMessage in for tickets
                        // written in another language; the original stays in
                        // the tooltip.
                        const ticketMessage = ticket.englishMessage || ticket.message;

                        return (
                          <TableRow key={ticket.id}>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{ticket.id}</TableCell>
                            <TableCell>{toDateLabel(ticket.createdAt)}</TableCell>
                            <TableCell>{TICKET_TYPE_LABELS[ticket.type] || ticket.type}</TableCell>
                            <TableCell>{statusChip(ticket.status)}</TableCell>
                            <TableCell title={ticket.originalMessage || ticketMessage}>
                              {truncate(ticketMessage)}
                            </TableCell>
                            {isSuperuser ? (
                              <TableCell>
                                {ticket.status !== 'resolved' ? (
                                  <Button size="small" onClick={() => openResolveDialog(ticket)}>
                                    Resolve
                                  </Button>
                                ) : null}
                              </TableCell>
                            ) : null}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={Boolean(resolvingTicket)} onClose={closeResolveDialog} fullWidth maxWidth="sm">
        <DialogTitle>Resolve Ticket</DialogTitle>
        <DialogContent>
          <TextField
            label="Resolution"
            value={resolutionNote}
            onChange={(event) => setResolutionNote(event.target.value)}
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResolveDialog} disabled={resolving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleResolve}
            disabled={resolving || !resolutionNote.trim()}>
            Resolve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Support;
